import React, { useState } from 'react';

import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TableHead from '@material-ui/core/TableHead';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import classNames from 'classnames';

import TableLIstItem from './TableLIstItem';
import locales from '../../locales';
import Title from '../Common/Title';
import { ITable, THallList, THall } from '../../types/IRestaurantTablesStore';
import { customErrorShackBar } from '../../actions/snackbars/SnackBarsActions';
import { makeStyles, Theme } from "@material-ui/core/styles";
import Circle from '@material-ui/icons/FiberManualRecord';
import DropUp from '@material-ui/icons/ArrowDropUp';
import DropDown from '@material-ui/icons/ArrowDropDown';
import FlashActive from '../Common/Icons/FlashActive';
import FlashInactive from '../Common/Icons/FlashInactive';
import EditField from '../Common/SelfSavingFields/EditField';
import { parseTableHall } from '../../helpers/bookingHelpers';

interface IProps {
  hallList: THallList;
  handleUpdateTable: (item: ITable | Array<ITable>) => void;
  customErrorShackBar: typeof customErrorShackBar;
  isAutoBookingEnabled: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    maxWidth: '100%'
  },
  // Десктопная версия
  desktopPaper: {
    [theme.breakpoints.down(960)]: {
      display: 'none',
    },
  },
  tableWrapper: {
    overflow: "auto",
    maxWidth: "100%"
  },
  table: {
    minWidth: "500px"
  },
  // Мобильная версия
  mobileCard: {
    display: 'none',
    [theme.breakpoints.down(960)]: {
      display: 'block',
    },
  },
  card: {
    borderRadius: 8,
    border: '1px solid #E5E5E5',
    width: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
  },
  cardContent: {
    padding: 0,
    width: '100%',
    boxSizing: 'border-box',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: 0,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardHeaderActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  tableItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBlock: theme.spacing(1),
    borderBottom: '1px solid #E5E5E5',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  tableItemInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  tableItemLabel: {
    fontSize: 12,
    color: '#AAAAAA',
    fontWeight: 500,
  },
  tableItemValue: {
    fontSize: 14,
    color: theme.palette.text.primary,
  },
  tableItemActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  mobileTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
  },
  tableListItemCheckBox: {
    padding: 0,
  },
}))

/**
 * Мобильная версия компонента зала
 */
const MobileHallCard: React.FC<{
  hall: THall;
  first: boolean;
  handleUpdateTable: (item: ITable | Array<ITable>) => void;
  customErrorShackBar: typeof customErrorShackBar;
  isAutoBookingEnabled: boolean;
  classes: ReturnType<typeof useStyles>;
}> = ({
  hall,
  first,
  handleUpdateTable,
  customErrorShackBar,
  isAutoBookingEnabled,
  classes,
}) => {
  const [open, changeList] = useState<boolean>(first);

  const handleOpenList = () => {
    changeList(!open);
  };

  const CircleActive = <Circle color="secondary" style={{ fontSize: 28 }} />;
  const CircleInactive = <Circle color="primary" style={{ fontSize: 28 }} />;
  const IconFlashActive = <FlashActive style={{ fontSize: 20 }} />;
  const IconFlashInactive = <FlashInactive style={{ fontSize: 20 }} />;

  /** @description Редактирование вместимости стола */
  const handleChangeCapacity = (newData: any, table: ITable) => {
    handleUpdateTable({
      ...table,
      capacity: parseInt(newData.value),
    });
  };

  /** @description Переключение типа бронирования для всех столов */
  const handleChangeAllBooking = (type: 'isBookingAllowed' | 'isAutoBookingAllowed') => {
    const newState = !hall[type];

    if (type === 'isAutoBookingAllowed' && !isAutoBookingEnabled) {
      customErrorShackBar(locales.t('settings.errors.updateTables.booking'));
    } else if (type === 'isBookingAllowed' && !newState) {
      const updatedTables = hall.tables.map(i => ({
        ...i,
        [type]: newState,
        isAutoBookingAllowed: false,
      }));

      handleUpdateTable(updatedTables);
    } else {
      const updatedTables = hall.tables.map((i) => {
        if (type === 'isAutoBookingAllowed' && newState) {
          return {
            ...i,
            [type]: i.isBookingAllowed ? newState : false,
          };
        }

        return {
          ...i,
          [type]: newState,
        };
      });

      handleUpdateTable(updatedTables);
    }
  };

  /** @description Переключение типа брониварования для выбранного стола */
  const handleChangeBooking = (
    bookingType: 'isAutoBookingAllowed' | 'isBookingAllowed', table: ITable,
  ) => {
    const newState = !table[bookingType];

    if (bookingType === 'isAutoBookingAllowed' && !isAutoBookingEnabled) {
      customErrorShackBar(locales.t('settings.errors.updateTables.booking'));
    } else if (bookingType === 'isBookingAllowed' && !newState) {
      handleUpdateTable({
        ...table,
        [bookingType]: !table[bookingType],
        isAutoBookingAllowed: false,
      });
    } else {
      handleUpdateTable({
        ...table,
        [bookingType]: !table[bookingType],
      });
    }
  };

  return (
    <Card className={classes.card} elevation={0}>
      <CardContent className={classes.cardContent}>
        <div className={classes.cardHeader}>
          <Typography className={classes.cardTitle}>
            {hall.hall}
          </Typography>
          <div className={classes.cardHeaderActions}>
            <Checkbox
              className={classes.tableListItemCheckBox}
              onClick={event => event.stopPropagation()}
              icon={CircleActive}
              checkedIcon={CircleInactive}
              checked={hall.isBookingAllowed}
              onChange={() => handleChangeAllBooking('isBookingAllowed')}
            />
            <Checkbox
              className={classes.tableListItemCheckBox}
              onClick={event => event.stopPropagation()}
              icon={IconFlashInactive}
              checkedIcon={IconFlashActive}
              checked={hall.isAutoBookingAllowed}
              onChange={() => handleChangeAllBooking('isAutoBookingAllowed')}
            />
            <IconButton onClick={handleOpenList} size="small">
              {open ? <DropUp /> : <DropDown />}
            </IconButton>
          </div>
        </div>
        {open && hall.tables.map(item => (
          <div key={item.hall + item.number} className={classes.tableItem}>
            <div className={classes.tableItemInfo}>
              <Typography className={classes.tableItemLabel}>
                {locales.t('settings.tableList.number')}
              </Typography>
              <Typography className={classes.tableItemValue}>
                {`${parseTableHall(item.hall)} ${item.number}`}
              </Typography>
            </div>
            <div className={classes.tableItemInfo}>
              <Typography className={classes.tableItemLabel}>
                {locales.t('settings.tableList.capacity')}
              </Typography>
              <EditField
                type={item.number}
                value={item.capacity}
                handleChangeList={(newTableData: any) => handleChangeCapacity(newTableData, item)}
                alignCenter
              />
            </div>
            <div className={classes.tableItemActions}>
              <Checkbox
                className={classes.tableListItemCheckBox}
                icon={CircleActive}
                checkedIcon={CircleInactive}
                checked={item.isBookingAllowed}
                onChange={() => handleChangeBooking('isBookingAllowed', item)}
              />
              <Checkbox
                className={classes.tableListItemCheckBox}
                disabled={!item.isBookingAllowed}
                icon={IconFlashInactive}
                checkedIcon={IconFlashActive}
                checked={item.isAutoBookingAllowed}
                onChange={() => handleChangeBooking('isAutoBookingAllowed', item)}
              />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

/**
 * @param classes
 * @param hallList Список столов ресторана
 * @param handleUpdateTable Ручка для редактирования колчества мест у стола
 */
const Index: React.FC<IProps> = ({
  handleUpdateTable,
  hallList,
  customErrorShackBar,
  isAutoBookingEnabled,
}) => {
  const classes = useStyles();
  return(
    <>
      {/* Десктопная версия (таблица) */}
      <Paper elevation={3} className={classNames(classes.wrapper, classes.desktopPaper)}>
        <Title
          text={locales.t('settings.tableList.title')}
          borderBottom
          helps={locales.t('settings.tableList.tooltip')}
        />
        <div className={classes.tableWrapper}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: '30%' }} padding="default">
                  {locales.t('settings.tableList.number')}
                </TableCell>
                <TableCell padding="default" align="center">
                  {locales.t('settings.tableList.capacity')}
                </TableCell>
                <TableCell padding="default" align="center">
                  {locales.t('settings.tableList.availableForBooking')}
                </TableCell>
                <TableCell padding="none" align="center">
                  {locales.t('settings.tableList.autoBooking')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {
                hallList.map((h, i) => (
                  <TableLIstItem
                    key={h.hall}
                    hall={h}
                    first={i === 0}
                    handleUpdateTable={handleUpdateTable}
                    customErrorShackBar={customErrorShackBar}
                    isAutoBookingEnabled={isAutoBookingEnabled}
                  />
                ))
              }
              <TableRow>
                <TableCell style={{ height: 54 }}>...</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </Paper>

      {/* Мобильная версия (карточки) */}
      <Card className={classes.mobileCard} elevation={0}>
        <CardContent className={classes.cardContent}>
          <Typography className={classes.mobileTitle}>
            {locales.t('settings.tableList.title')}
          </Typography>
          
          {hallList.map((h, i) => (
            <MobileHallCard
              key={h.hall}
              hall={h}
              first={i === 0}
              handleUpdateTable={handleUpdateTable}
              customErrorShackBar={customErrorShackBar}
              isAutoBookingEnabled={isAutoBookingEnabled}
              classes={classes}
            />
          ))}
        </CardContent>
      </Card>
    </>
  )
};

export default Index;
